import React, { Component, Fragment } from 'react';
import { Authenticated, Title } from 'react-admin';

import BigCalendar from 'react-big-calendar';
import moment from 'moment';

import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import PaintIcon from '@material-ui/icons/FormatPaint';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import dataProvider from '../dataProvider';

import CalendarFilters from './CalendarFilters';
import ZippityEvent from './ZippityEvent';
import NewBookingDialog from './NewBookingDialog';
import CalendarNotesDialog from './CalendarNotesDialog';
import CopyBookingDialog from './CopyBookingDialog';
import DetailDialog from './DetailDialog';

import getFullness from './bookingStatHelper';
import {
  getDateArray,
  getServiceEntity,
  getZ3PConfigValue,
  localStorageGet,
} from '../helper';

import './react-big-calendar.css';

import {
  getColorFromFullness,
  getCompatibleTextColor,
  ZIPPITY_COLORS,
} from './colors';
import { apiGet } from '../brainApi';

const CALENDAR_DEFAULT_VIEW = 'work_week';

export default class Calendar extends Component {
  static legendEntry(emoji, name) {
    return (
      <Fragment key={name}>
        <span role="img" aria-label={name} style={{ marginLeft: '15px' }}>
          {emoji}
        </span>
        = {name}
      </Fragment>
    );
  }

  // Convert a String to an Integer, using its character values
  static stringToInt(string) {
    let result = 0;
    if (!string) {
      return result;
    }
    // Multiply by (length - index) and 128, so that ASCII strings of the same length are sorted alphabetically.
    // (If it's non-ASCII, it still returns a consistent sorting.)
    for (let i = 0; i < string.length; i += 1) {
      result += string.charCodeAt(i) * (string.length - i) * 128;
    }
    return result;
  }

  static deltaDays(date, delta) {
    return moment(date)
      .add(delta, 'days')
      .format('YYYY-MM-DD');
  }

  static defaultDateRange() {
    switch (CALENDAR_DEFAULT_VIEW) {
      case 'day':
        return [new Date()];
      case 'work_week':
        return getDateArray(
          moment()
            .startOf('week')
            .add('days', 1),
          moment()
            .endOf('week')
            .add('days', -1),
        );
      case 'week':
        return getDateArray(moment().startOf('week'), moment().endOf('week'));
      case 'month':
        return getDateArray(
          moment()
            .startOf('month')
            .startOf('week'),
          moment()
            .endOf('month')
            .endOf('week'),
        );
      default:
        return [];
    }
  }

  constructor(props) {
    super(props);
    const calendarColor = getZ3PConfigValue('calendar_color');
    this.state = {
      currentDate: new Date(),
      dateRange: Calendar.defaultDateRange(),
      scheduleEvents: [],
      filteredEvents: [],
      serviceVehicleIdToColor: {},
      newBookingDialogOpen: false,
      newBookingDate: null,
      newBookingServiceVehicleId: null,
      copyBookingDialogOpen: false,
      copyScheduleInstance: null,
      detailDialogOpen: false,
      calendarNotesDialogOpen: false,
      selectedEvent: null,
      allServiceVehicleIds: [],
      allServiceVehicles: [],
      allClientLocations: {},
      checkedForClientLocations: false,
      selectedServiceVehicles: new Set(),
      selectedServiceTypes: new Set(),
      selectedClientLocationIds: new Set(),
      colorFormat: calendarColor,
      calendarNotes: {},
      currentEvent: {},
      userInfo: null,
      scheduleTypeNameToEmoji: {},
    };
  }

  componentDidMount() {
    const { currentDate } = this.state;
    const startDate = moment(currentDate)
      .startOf('week')
      .format('YYYY-MM-DD');
    const endDate = moment(currentDate)
      .endOf('week')
      .format('YYYY-MM-DD');

    // TODO: This is inefficient to load all these in sequence.
    // Stop doing this after we start sending home_address from Brain2.
    this.getServiceVehicles().then(() => {
      this.getClientLocations().then(() => {
        this.refreshCalendarEvents(startDate, endDate);
      });
    });
    this.getScheduleTypes();
    this.setState({ userInfo: localStorageGet('user') });
  }

  onRangeChange = (range) => {
    // react-big-calendar has 2 different ways of sending range changes
    const startDate = range[0] || range.start;
    const endDate = range[range.length - 1] || range.end;
    this.setState(
      {
        currentDate: startDate,
        dateRange: getDateArray(startDate, endDate),
      },
      () =>
        this.refreshCalendarEvents(
          moment(startDate).format('YYYY-MM-DD'),
          moment(endDate).format('YYYY-MM-DD'),
        ),
    );
  };

  onNavigate = (startDate) => {
    const { dateRange } = this.state;
    const dateRangeSize = dateRange.length;
    const endDate = Calendar.deltaDays(startDate, dateRangeSize - 1);
    this.setState(
      {
        currentDate: startDate,
        dateRange: getDateArray(startDate, endDate),
      },
      this.getCalendarNotes,
    );
  };

  getServiceVehicles = () => {
    return dataProvider
      .getList('service-vehicle', {
        filter: { is_active: true },
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 500 },
      })
      .then((response) => {
        const serviceVehicleIds = response.data.map(
          (v) => v.service_vehicle_id,
        );
        this.setState(
          {
            allServiceVehicleIds: serviceVehicleIds,
            allServiceVehicles: response.data,
            selectedServiceVehicles: new Set(serviceVehicleIds),
          },
          this.recalculateFilter,
        );
      });
  };

  getClientLocations = () => {
    return dataProvider
      .getList('client-location', {
        filter: {},
        sort: { field: 'long_name', order: 'ASC' },
        pagination: { page: 1, perPage: 500 },
      })
      .then((response) => {
        const clientLocationIdToDetails = {};
        response.data.forEach((location) => {
          clientLocationIdToDetails[location.client_location_id] = location;
        });
        this.setState(
          {
            allClientLocations: clientLocationIdToDetails,
            checkedForClientLocations: true,
            selectedClientLocationIds: new Set(
              Object.keys(clientLocationIdToDetails),
            ),
          },
          this.recalculateFilter,
        );
      });
  };

  getScheduleTypes = () => {
    dataProvider
      .getList('schedule-type', {
        filter: {},
        sort: { field: 'name', order: 'ASC' },
        pagination: { page: 1, perPage: 500 },
      })
      .then((response) => {
        const scheduleTypeNameToEmoji = {};
        response.data.forEach(({ name, emoji }) => {
          scheduleTypeNameToEmoji[name] = emoji;
        });
        this.setState({
          scheduleTypeNameToEmoji,
          selectedServiceTypes: new Set(Object.keys(scheduleTypeNameToEmoji)),
        });
      });
  };

  getCalendarNotes = () => {
    const { dateRange } = this.state;
    const dateStart = moment(dateRange[0]).format('YYYY-MM-DD');
    const dateEnd = moment(dateRange[dateRange.length - 1]).format(
      'YYYY-MM-DD',
    );
    const params = { date_start: dateStart, date_end: dateEnd };

    apiGet('/calendar-note', params)
      .then((json) => {
        this.setState({ calendarNotes: json });
      })
      .then(() => this.setCalendarNotes())
      .catch((error) => {
        console.log(error);
      });
  };

  setCalendarNotes = () => {
    const { filteredEvents } = this.state;
    for (let i = 0; i < filteredEvents.length; i += 1) {
      const event = filteredEvents[i];
      const { serviceVehicleId } = event;
      const date = moment(event.start).format('YYYY-MM-DD');
      event.calendarNotes = this.calendarNotes(serviceVehicleId, date);
    }
  };

  setSelectedClientLocations = (selectedClientLocationIds) => {
    this.setState(
      { selectedClientLocationIds: new Set(selectedClientLocationIds) },
      this.recalculateFilter,
    );
  };

  setSelectedServiceTypes = (selectedServiceTypes) => {
    this.setState(
      { selectedServiceTypes: new Set(selectedServiceTypes) },
      this.recalculateFilter,
    );
  };

  setSelectedServiceVehicles = (selectedServiceVehicles) => {
    this.setState(
      { selectedServiceVehicles: new Set(selectedServiceVehicles) },
      this.recalculateFilter,
    );
  };

  static getEventClassName(event) {
    const sevenDaysAgo = moment().subtract(7, 'days');
    const createdAt = event.scheduleInstance.created_at;
    const isRecent = moment(createdAt).isAfter(sevenDaysAgo);
    const isEmpty = event.fullnessFilter === 'dummyAlign';
    const isDemandBased = event.scheduleInstance.is_demand_based
      ? 'isDemandBased'
      : '';

    return `${isEmpty && 'emptyEvent'} ${isRecent &&
      'recentlyCreated'} ${isDemandBased}`;
  }

  getEventStyle(event) {
    if (event.fullnessFilter === 'dummyAlign') return null;
    const backgroundColor = this.getEventColor(event);
    const color = getCompatibleTextColor(backgroundColor);
    return { backgroundColor, color };
  }

  getEventColor({ serviceVehicleId, scheduleInstance }) {
    const { colorFormat, serviceVehicleIdToColor } = this.state;
    if (colorFormat === 'serviceVehicle') {
      return serviceVehicleIdToColor[serviceVehicleId];
    }
    const hours = getFullness(scheduleInstance);

    return getColorFromFullness(hours);
  }

  // If the user clicked a specific day, select that in the date picker.
  toggleNewBookingDialog = ({ start }, serviceVehicleId = null) => {
    const { newBookingDialogOpen } = this.state;
    const date = start ? moment(start).format('YYYY-MM-DD') : null;
    this.setState({
      newBookingDialogOpen: !newBookingDialogOpen,
      newBookingDate: date,
      newBookingServiceVehicleId: serviceVehicleId,
    });
  };

  toggleDetailDialog = (event) => {
    const { detailDialogOpen } = this.state;
    this.setState({
      detailDialogOpen: !detailDialogOpen,
      selectedEvent: event,
    });
  };

  toggleCalendarNotesDialog = (event) => {
    this.setState({
      calendarNotesDialogOpen: true,
      currentEvent: event,
    });
  };

  eventSelected = (event) => {
    if (event.fullnessFilter === 'dummyAlign') {
      this.toggleNewBookingDialog(
        { start: event.start },
        event.serviceVehicleId,
      );
      return;
    }
    this.toggleDetailDialog(event);
  };

  // TODO: Possibly combine copyBookingDialog and newBookingDialog
  // TODO: Use "activeDialog" instead of 3 booleans

  assignColors = (strings) => {
    const { serviceVehicleIdToColor } = this.state;
    const stringToColor = serviceVehicleIdToColor;
    [...new Set(strings)].forEach((str) => {
      if (!stringToColor[str]) {
        const index = Object.keys(stringToColor).length % ZIPPITY_COLORS.length;
        stringToColor[str] = ZIPPITY_COLORS[index];
      }
    });
    return stringToColor;
  };

  // Allow other components to update events on the calendar
  updateCalendar = (json) => {
    const { scheduleEvents } = this.state;
    let newScheduleEvents = scheduleEvents;
    json.forEach((si) => {
      const { is_canceled: isCanceled, schedule_instance_id: siId } = si;
      newScheduleEvents = newScheduleEvents.filter(
        (event) => event.scheduleInstance.schedule_instance_id !== siId,
      );
      if (!isCanceled) {
        const updatedEvent = this.createEvent(si);
        newScheduleEvents.push(updatedEvent);
      }
    });

    this.setState(
      { scheduleEvents: newScheduleEvents },
      this.recalculateFilter,
    );
  };

  recalculateFilter = () => {
    const {
      selectedServiceVehicles,
      scheduleEvents,
      selectedServiceTypes,
      selectedClientLocationIds,
      colorFormat,
    } = this.state;
    // Delete any dummy events
    const realScheduleEvents = scheduleEvents
      .filter((schEvents) => schEvents.fullnessFilter !== 'dummyAlign')
      .map((e) => ({ ...e, fullnessFilter: colorFormat }));

    const matchesClientLocation = (event) => {
      const { scheduleInstance } = event;
      const clientLocationIds = scheduleInstance.client_locations.map(
        (cl) => cl.client_location_id,
      );
      // We always want to show home-only schedule instances
      if (clientLocationIds.length < 1) return true;
      const intersection = clientLocationIds.filter((clientLocationId) =>
        selectedClientLocationIds.has(clientLocationId.toString()),
      );
      return !!intersection.length;
    };

    const matchesServiceType = ({ scheduleInstance }) => {
      const eventTypes = scheduleInstance.offered_service_types;
      const intersection = eventTypes.filter((serviceType) =>
        selectedServiceTypes.has(serviceType),
      );
      return !!intersection.length;
    };

    const filteredEvents = realScheduleEvents
      .filter(({ serviceVehicleId }) =>
        selectedServiceVehicles.has(serviceVehicleId),
      )
      .filter((event) => matchesClientLocation(event))
      .filter((event) => matchesServiceType(event));

    this.alignEvents(filteredEvents);
  };

  handleClose = () => {
    this.setState({
      newBookingDialogOpen: false,
      calendarNotesDialogOpen: false,
      copyBookingDialogOpen: false,
      detailDialogOpen: false,
      selectedEvent: null,
    });
  };

  refreshCalendarEvents = (startDate, endDate) => {
    // Event notes are set within both getCalendarNotes and the dataProvider
    // This ensures that the notes will be set regardless of which parallel fetch finishes first
    this.getCalendarNotes();
    dataProvider
      .getList('schedule/stats', {
        filter: {
          start_time_after: startDate,
          end_time_before: endDate,
        },
        sort: { field: 'date', order: 'ASC' },
        pagination: { page: 1, perPage: 2000 },
      })
      .then((response) => {
        const events = this.createEvents(response.data);
        const allServiceVehicleIds = response.data.map(
          ({ service_vehicle_id }) => service_vehicle_id,
        );
        const serviceVehicleIdToColor = this.assignColors(allServiceVehicleIds);
        this.setState(
          {
            scheduleEvents: events,
            serviceVehicleIdToColor,
          },
          this.recalculateFilter,
        );
      });
  };

  // return the calendar notes for a specific service vehicle and date combination
  calendarNotes = (serviceVehicleId, date) => {
    const { calendarNotes } = this.state;
    const dateStringKey = moment(date).format('YYYY-MM-DD');
    let calNotes = [];
    if (
      calendarNotes[serviceVehicleId] &&
      calendarNotes[serviceVehicleId][dateStringKey]
    ) {
      calNotes = calendarNotes[serviceVehicleId][dateStringKey];
    }
    calNotes.sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
    return calNotes;
  };

  createEvent = (scheduleInstance) => {
    const {
      colorFormat,
      scheduleTypeNameToEmoji,
      allServiceVehicles,
    } = this.state;
    const {
      service_vehicle_id: serviceVehicleId,
      start_time: startTime,
    } = scheduleInstance;
    // This is a hack to force the events to be sorted by serviceVehicleId.
    // If react-big-calendar starts supporting custom sorting methods, do that instead.
    const MILLIS_PER_DAY = 1000 * 60 * 60 * 24;
    const delta = Calendar.stringToInt(serviceVehicleId) % MILLIS_PER_DAY;

    const date = moment(startTime).toDate();
    date.setTime(date.getTime() + delta);

    const serviceVehicle = allServiceVehicles.find(
      ({ id }) => id === serviceVehicleId,
    );

    return {
      start: date,
      end: date,
      allDay: true,
      serviceVehicleId,
      calendarNotes: this.calendarNotes(serviceVehicleId, date),
      onClickCalendarNotes: (event) => {
        this.toggleCalendarNotesDialog(event);
      },
      scheduleInstance,
      canceledScheduleInstances: [],
      fullnessFilter: colorFormat,
      scheduleTypeNameToEmoji,
      serviceVehicle,
    };
  };

  createEvents = (scheduleInstances) => {
    const filteredSchedules = scheduleInstances.filter(
      (sch) => !sch.is_canceled,
    );
    const events = filteredSchedules.map((sch) => this.createEvent(sch));
    return events;
  };

  alignEvents = (filteredEvents) => {
    const { dateRange, allServiceVehicles } = this.state;
    // Delete any dummy events
    const alignedEvents = filteredEvents;
    // First, we build a matrix of how many rows we need for each SV-Date combo
    const allServiceVehicleIds = new Set(
      filteredEvents.map(
        ({ scheduleInstance }) => scheduleInstance.service_vehicle_id,
      ),
    );

    const serviceVehicleIdToVehicle = {};
    allServiceVehicles.forEach((sv) => {
      serviceVehicleIdToVehicle[sv.id] = sv;
    });

    const alignmentMatrix = {};

    dateRange.forEach((date) => {
      allServiceVehicleIds.forEach((serviceVehicleId) => {
        const filteredSchEvents = filteredEvents.filter(
          (schEvent) =>
            moment(schEvent.scheduleInstance.start_time).isSame(date, 'day') &&
            schEvent.scheduleInstance.service_vehicle_id === serviceVehicleId,
        );
        if (
          alignmentMatrix[serviceVehicleId] === undefined ||
          alignmentMatrix[serviceVehicleId] < filteredSchEvents.length
        ) {
          alignmentMatrix[serviceVehicleId] = filteredSchEvents.length;
        }
      });
    });

    // then we populate missing slots with empty events
    dateRange.forEach((date) => {
      allServiceVehicleIds.forEach((serviceVehicleId) => {
        const filteredSchEvents = filteredEvents.filter(
          (schEvent) =>
            moment(schEvent.scheduleInstance.start_time).isSame(date, 'day') &&
            schEvent.scheduleInstance.service_vehicle_id === serviceVehicleId,
        );
        const fakeEventsNeeded =
          alignmentMatrix[serviceVehicleId] - filteredSchEvents.length;
        for (let i = 0; i < fakeEventsNeeded; i += 1) {
          const MILLIS_PER_DAY = 1000 * 60 * 60 * 24;
          const delta = Calendar.stringToInt(serviceVehicleId) % MILLIS_PER_DAY;
          const sortableDate = moment(date).toDate();
          sortableDate.setTime(sortableDate.getTime() + delta);
          alignedEvents.push({
            start: sortableDate,
            end: sortableDate,
            allDay: true,
            serviceVehicle: serviceVehicleIdToVehicle[serviceVehicleId],
            serviceVehicleId,
            calendarNotes: this.calendarNotes(serviceVehicleId, date),
            onClickCalendarNotes: (event) => {
              this.toggleCalendarNotesDialog(event);
            },
            scheduleInstance: { start_time: date, end_time: date },
            canceledScheduleInstances: [],
            fullnessFilter: 'dummyAlign',
          });
        }
      });
    });
    this.setState({ filteredEvents: alignedEvents });
  };

  handleCopy = (scheduleInstance) => {
    this.setState({
      detailDialogOpen: false,
      copyBookingDialogOpen: true,
      copyScheduleInstance: scheduleInstance,
    });
  };

  render() {
    const {
      formatAnchor,
      allServiceVehicleIds,
      allServiceVehicles,
      allClientLocations,
      checkedForClientLocations,
      selectedEvent,
      detailDialogOpen,
      newBookingDate,
      newBookingServiceVehicleId,
      newBookingDialogOpen,
      copyScheduleInstance,
      copyBookingDialogOpen,
      selectedServiceVehicles,
      selectedServiceTypes,
      currentDate,
      filteredEvents,
      selectedClientLocationIds,
      calendarNotesDialogOpen,
      currentEvent,
      userInfo,
      scheduleTypeNameToEmoji,
    } = this.state;

    const { location } = this.props;

    const components = {
      event: ZippityEvent,
    };

    return (
      <Authenticated location={location}>
        <div className="custom-container Calendar-container">
          <Title title="resources.calendar.title" />
          <span>
            <Button
              color="primary"
              aria-owns="format-menu"
              aria-haspopup="true"
              onClick={(e) => this.setState({ formatAnchor: e.currentTarget })}
            >
              <PaintIcon style={{ marginRight: '7px' }} />
              Change Colors
            </Button>

            <Menu
              id="format-menu"
              anchorEl={formatAnchor}
              open={Boolean(formatAnchor)}
              onClose={() => this.setState({ formatAnchor: null })}
            >
              <MenuItem
                onClick={() =>
                  this.setState(
                    { colorFormat: 'fullness', formatAnchor: null },
                    this.recalculateFilter,
                  )
                }
              >
                Time Utilization
              </MenuItem>
              <MenuItem
                onClick={() =>
                  this.setState({
                    colorFormat: 'serviceVehicle',
                    formatAnchor: null,
                  })
                }
              >
                {getServiceEntity()}
              </MenuItem>
            </Menu>
          </span>
          {!!allServiceVehicleIds.length && (
            <CalendarFilters
              allServiceVehicleIds={allServiceVehicleIds}
              allServiceVehicles={allServiceVehicles}
              allServiceTypes={scheduleTypeNameToEmoji}
              allClientLocations={allClientLocations}
              setSelectedServiceVehicles={this.setSelectedServiceVehicles}
              setSelectedServiceTypes={this.setSelectedServiceTypes}
              setSelectedClientLocations={this.setSelectedClientLocations}
              selectedServiceVehicles={selectedServiceVehicles}
              selectedServiceTypes={selectedServiceTypes}
              selectedClientLocationIds={selectedClientLocationIds}
            />
          )}
          <Button color="primary" onClick={this.toggleNewBookingDialog}>
            <AddIcon style={{ marginRight: '7px' }} />
            Create
          </Button>
          <BigCalendar
            localizer={BigCalendar.momentLocalizer(moment)}
            defaultView={CALENDAR_DEFAULT_VIEW}
            views={{
              month: true,
              week: true,
              work_week: true,
              day: true,
            }}
            events={filteredEvents}
            eventPropGetter={(event) => ({
              style: this.getEventStyle(event),
              className: Calendar.getEventClassName(event),
            })}
            defaultDate={currentDate}
            onRangeChange={this.onRangeChange}
            onNavigate={this.onNavigate}
            onSelectEvent={this.eventSelected}
            components={components}
            onSelectSlot={this.toggleNewBookingDialog}
            selectable
          />
          <div>
            <p>
              Legend:
              {Object.keys(scheduleTypeNameToEmoji).map((name) =>
                Calendar.legendEntry(scheduleTypeNameToEmoji[name], name),
              )}
            </p>
          </div>
          {!!allServiceVehicleIds.length &&
            !calendarNotesDialogOpen &&
            checkedForClientLocations && (
              <NewBookingDialog
                open={newBookingDialogOpen}
                onClose={this.handleClose}
                updateCalendar={this.updateCalendar}
                date={newBookingDate}
                defaultServiceVehicleId={newBookingServiceVehicleId}
                allServiceVehicleIds={allServiceVehicleIds}
                allServiceVehicles={allServiceVehicles}
                allClientLocations={allClientLocations}
                scheduleTypeNameToEmoji={scheduleTypeNameToEmoji}
                setDateMethod={(date) =>
                  this.setState({ newBookingDate: date })
                }
                clearDefaultServiceVehicleIdMethod={() =>
                  this.setState({ newBookingServiceVehicleId: null })
                }
              />
            )}
          {!!calendarNotesDialogOpen && (
            <CalendarNotesDialog
              open={calendarNotesDialogOpen}
              onClose={this.handleClose}
              updateCalendar={this.updateCalendar}
              date={newBookingDate}
              event={currentEvent}
              userInfo={userInfo}
            />
          )}
          <CopyBookingDialog
            open={copyBookingDialogOpen}
            onClose={this.handleClose}
            updateCalendar={this.updateCalendar}
            scheduleInstance={copyScheduleInstance}
            allServiceVehicles={allServiceVehicles}
          />
          {selectedEvent && !calendarNotesDialogOpen && (
            <DetailDialog
              open={detailDialogOpen}
              event={selectedEvent}
              onClose={this.handleClose}
              updateCalendar={this.updateCalendar}
              allClientLocations={allClientLocations}
              allScheduleTypes={Object.keys(scheduleTypeNameToEmoji)}
              handleCopy={this.handleCopy}
            />
          )}
        </div>
      </Authenticated>
    );
  }
}
